import React, { useMemo, useState } from "react";
import { FiX, FiArrowLeft, FiCheck, FiAlertTriangle, FiInfo, FiZap } from "react-icons/fi";
import { userRequest } from "../../../utils/requestMethods";
import { parseQuestionsFromText } from "../../../utils/questionExportImport";

/**
 * Paste questions copied from Word, Google Docs or a PDF and add them to the test.
 *
 * The text is converted on the server (POST /question/parse), which reads messy layouts —
 * options on one line, answer keys at the end, mixed question types. Nothing is saved there:
 * the questions are added to the editor on this page and saved with the page's own Save.
 * If the server can't convert (not set up, or failing), the older in-browser parser is
 * offered instead.
 */

const MAX_CHARS = 50000;

const TYPE_BADGES = {
  multiple_choice: { label: "Multiple Choice", color: "bg-blue-100 text-blue-700" },
  true_or_false: { label: "True / False", color: "bg-green-100 text-green-700" },
  text: { label: "Open Answer", color: "bg-purple-100 text-purple-700" },
};

const PLACEHOLDER = `1. What is the capital of France?
A. London   B. Paris   C. Berlin   D. Rome
Answer: B

2. The Earth revolves around the Sun. (True/False)

3. Explain the process of photosynthesis.

ANSWER KEY: 1. B  2. True`;

// The page editors expect this shape (same as the spreadsheet and NABS imports).
const toEditorQuestion = (q, index) => ({
  id: Date.now() + index,
  question: q.question,
  type: q.type,
  options: q.type === "multiple_choice" ? q.options || [] : [],
  correct_answer: q.correct_answer || "",
  is_active: true,
  point: undefined,
});

const needsAnswer = (q) => q.type !== "text" && !q.correct_answer;

const serverMessage = (err) => {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  return data?.message || err?.message || "Something went wrong while converting the questions.";
};

const PasteQuestionsModal = ({ onClose, onConfirm, testId, schoolId }) => {
  const [rawText, setRawText] = useState("");
  const [step, setStep] = useState("input"); // "input" | "preview"
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState("");
  const [canUseBasic, setCanUseBasic] = useState(false);
  const [usedBasic, setUsedBasic] = useState(false);
  // Why the basic parser was used, when the server said so (a smart conversion limit).
  const [basicReason, setBasicReason] = useState("");
  // { test_remaining, test_limit, … } after a smart conversion
  const [allowance, setAllowance] = useState(null);
  // [{ question, issues, notes }]
  const [items, setItems] = useState([]);

  const tooLong = rawText.length > MAX_CHARS;

  const showResults = (questions, reviews, basic) => {
    setItems(
      questions.map((q, i) => ({
        question: toEditorQuestion(q, i),
        issues: reviews?.[i]?.issues || [],
        notes: reviews?.[i]?.notes || [],
      }))
    );
    setUsedBasic(basic);
    setStep("preview");
  };

  const convertWithBasicParser = (reason = "") => {
    setBasicReason(reason);
    setAllowance(null);
    const questions = parseQuestionsFromText(rawText);
    if (!questions.length) {
      setError("No questions were found in the pasted text. Check the format and try again.");
      setCanUseBasic(false);
      return;
    }
    setError("");
    showResults(questions, null, true);
  };

  const handleConvert = async () => {
    setError("");
    setCanUseBasic(false);

    if (!testId || !schoolId) {
      convertWithBasicParser();
      return;
    }

    setConverting(true);
    try {
      const { data } = await userRequest.post("/question/parse", {
        school: schoolId,
        test: testId,
        text: rawText,
      });
      if (data?.limit_reached) {
        // Out of smart conversions for this test or this month — no error, just the basic parser.
        convertWithBasicParser(data.limit_message);
        return;
      }
      if (!data?.questions?.length) {
        setError("No questions were found in the pasted text.");
        setCanUseBasic(true);
        return;
      }
      setBasicReason("");
      setAllowance(data.allowance || null);
      showResults(data.questions, data.review, false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503 && /not configured/i.test(serverMessage(err))) {
        // Smart conversion isn't set up on this server — fall back without a fuss.
        convertWithBasicParser();
        return;
      }
      setError(serverMessage(err));
      // Permission or test problems won't be fixed by another parser.
      setCanUseBasic(![400, 403, 404, 409].includes(status));
    } finally {
      setConverting(false);
    }
  };

  const setAnswer = (index, answer) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, question: { ...item.question, correct_answer: answer } } : item
      )
    );
  };

  const counts = useMemo(() => {
    const unanswered = items.filter((item) => needsAnswer(item.question)).length;
    const withIssues = items.filter((item) => item.issues.length > 0).length;
    return { unanswered, withIssues };
  }, [items]);

  const handleConfirm = () => {
    onConfirm(items.map((item) => item.question));
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {step === "preview" && (
              <button
                onClick={() => setStep("input")}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to the pasted text"
              >
                <FiArrowLeft size={18} />
              </button>
            )}
            <h2 className="text-lg font-semibold text-gray-900">
              {step === "input" ? "Paste Questions" : `Review Questions (${items.length} found)`}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <FiX size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {step === "input" ? (
            <>
              <p className="text-sm text-gray-500 mb-3">
                Paste questions from Word, Google Docs or a PDF. Numbering, options on one line, answers under each
                question or in an answer key at the end are all understood.
              </p>

              <textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder={PLACEHOLDER}
                rows={14}
                disabled={converting}
                className="w-full border border-gray-200 rounded-xl p-4 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
              />
              <div className={`mt-1 text-right text-xs ${tooLong ? "text-red-600" : "text-gray-400"}`}>
                {rawText.length.toLocaleString()} / {MAX_CHARS.toLocaleString()} characters
                {tooLong && " — split the text and paste it in parts"}
              </div>

              {error && (
                <div className="mt-3 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                  <FiAlertTriangle className="flex-shrink-0 mt-0.5" size={14} />
                  <div className="flex-1">
                    <span>{error}</span>
                    {canUseBasic && (
                      <button
                        onClick={() => convertWithBasicParser()}
                        className="block mt-1 text-xs font-semibold underline"
                      >
                        Use basic conversion instead
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {usedBasic && (
                <div className="mb-4 flex items-start gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <FiInfo className="flex-shrink-0 mt-0.5" size={14} />
                  <span>
                    {basicReason ? `${basicReason} ` : "Converted with the basic parser. "}
                    It only understands simple layouts — check every question carefully.
                  </span>
                </div>
              )}

              {!usedBasic && allowance && (
                <div className="mb-4 flex items-start gap-2 text-xs text-gray-500">
                  <FiZap className="flex-shrink-0 mt-0.5" size={12} />
                  <span>
                    {allowance.test_remaining > 0
                      ? `${allowance.test_remaining} more smart conversion${allowance.test_remaining !== 1 ? "s" : ""} left for this test.`
                      : "This was the last smart conversion for this test — further pastes will use the basic parser."}
                  </span>
                </div>
              )}

              {counts.unanswered > 0 && (
                <div className="mb-4 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <FiAlertTriangle className="flex-shrink-0 mt-0.5" size={14} />
                  <span>
                    {counts.unanswered} question{counts.unanswered > 1 ? "s have" : " has"} no answer in the pasted
                    text. Click the correct option below, or set it in the editor after adding.
                  </span>
                </div>
              )}

              {counts.withIssues > 0 && (
                <div className="mb-4 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <FiAlertTriangle className="flex-shrink-0 mt-0.5" size={14} />
                  <span>
                    {counts.withIssues} question{counts.withIssues > 1 ? "s need" : " needs"} fixing in the editor
                    before the test can be saved — see the red notes below.
                  </span>
                </div>
              )}

              <div className="space-y-3">
                {items.map(({ question: q, issues, notes }, idx) => {
                  const badge = TYPE_BADGES[q.type] || TYPE_BADGES.text;
                  const missing = needsAnswer(q);
                  return (
                    <div
                      key={q.id}
                      className={`border rounded-xl p-4 ${
                        issues.length ? "border-red-200" : missing ? "border-amber-200" : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <p className="text-sm font-medium text-gray-800 whitespace-pre-line">
                          <span className="text-gray-400 mr-1.5">Q{idx + 1}.</span>
                          {q.question}
                        </p>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${badge.color}`}>
                          {badge.label}
                        </span>
                      </div>

                      {q.type === "multiple_choice" && q.options.length > 0 && (
                        <div className="ml-6 space-y-1 mb-2">
                          {q.options.map((opt, oi) => {
                            const isCorrect = opt === q.correct_answer;
                            return (
                              <button
                                key={oi}
                                type="button"
                                onClick={() => setAnswer(idx, opt)}
                                title="Mark as the correct answer"
                                className={`w-full text-left text-xs flex items-center gap-1.5 rounded px-1.5 py-0.5 transition-colors ${
                                  isCorrect
                                    ? "text-green-700 font-medium bg-green-50"
                                    : "text-gray-600 hover:bg-gray-100"
                                }`}
                              >
                                <span className="w-3 flex-shrink-0">{isCorrect && <FiCheck size={12} />}</span>
                                <span>
                                  {String.fromCharCode(65 + oi)}. {opt}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {q.type === "true_or_false" && (
                        <div className="ml-6 flex items-center gap-2 text-xs mb-2">
                          <span className="text-gray-500">Answer:</span>
                          {["true", "false"].map((value) => (
                            <button
                              key={value}
                              type="button"
                              onClick={() => setAnswer(idx, value)}
                              className={`px-2 py-0.5 rounded border transition-colors ${
                                q.correct_answer === value
                                  ? "border-green-500 bg-green-50 text-green-700 font-medium"
                                  : "border-gray-200 text-gray-600 hover:bg-gray-100"
                              }`}
                            >
                              {value === "true" ? "True" : "False"}
                            </button>
                          ))}
                        </div>
                      )}

                      {q.type === "text" && q.correct_answer && (
                        <div className="ml-6 text-xs text-gray-500 mb-2">
                          Expected: <span className="text-green-700 font-medium">{q.correct_answer}</span>
                        </div>
                      )}

                      {missing && <div className="ml-6 text-xs text-amber-600">No answer yet — click the correct one</div>}

                      {issues.map((issue, i) => (
                        <div key={`i${i}`} className="ml-6 mt-1 text-xs text-red-600">
                          {issue}
                        </div>
                      ))}
                      {notes.map((note, i) => (
                        <div key={`n${i}`} className="ml-6 mt-1 text-xs text-gray-400">
                          {note}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          {step === "input" ? (
            <button
              onClick={handleConvert}
              disabled={!rawText.trim() || tooLong || converting}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {converting ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Converting…
                </>
              ) : (
                <>
                  <FiZap size={14} />
                  Convert Questions
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleConfirm}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Add {items.length} Question{items.length !== 1 ? "s" : ""}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasteQuestionsModal;
